/**
 * The offline write path.
 *
 * Every local change goes through `mutate`. It writes the domain row and
 * appends the outbox operation inside ONE IndexedDB transaction. That
 * atomicity is the whole point of this module: a domain row that survives a
 * crash without its outbox entry is a rating the user can see on their screen
 * and that will never reach the server — silent data loss, and the exact
 * failure an adversarial review rejected the first design for.
 *
 * Storage sits behind `OutboxStorage` so the protocol can be tested against an
 * in-memory fake. IndexedDB does not exist in Bun's test runtime, and the
 * protocol — atomic pairing, op_id generation, conflict routing, dead-lettering
 * — is the part worth testing.
 */

import type { Entity, Mutation, MutationResult } from "../../shared/types.ts";
import { opId } from "../../shared/ids.ts";
import {
  CONFLICT_STORE,
  DEAD_STORE,
  OUTBOX_STORE,
  transaction,
  type StoreName,
} from "./idb.ts";

export type Row = Record<string, unknown>;

/** A queued mutation. `seq` is assigned by storage and defines drain order. */
export interface OutboxOp extends Mutation {
  seq?: number;
  created_at: number;
}

export type SidelineKind = "conflict" | "dead";

/** An op the server refused. Never discarded without the user seeing it. */
export interface SidelinedOp {
  op_id: string;
  kind: SidelineKind;
  op: OutboxOp;
  at: number;
  /** Conflicts only: what the server currently holds. */
  server_version?: number;
  server_row?: Row;
  /** Dead letters only. */
  reason?: string;
}

/**
 * The seam between the protocol and the disk. Implementations must make
 * `commit`, `settle` and `sideline` atomic — each is a single transaction.
 */
export interface OutboxStorage {
  /**
   * Write `row` into `entity` and, if `op` is given, append it to the outbox.
   * MUST be one transaction: both land or neither does.
   */
  commit(entity: Entity, row: Row, op: OutboxOp | null): Promise<void>;
  read(entity: Entity, id: string): Promise<Row | undefined>;
  /**
   * Every locally held row of one entity. Optional so that a hand-rolled
   * storage double keeps type-checking; callers must degrade to "no data"
   * rather than throwing when it is absent.
   */
  readAll?(entity: Entity): Promise<Row[]>;
  /** Queued ops in FIFO order. */
  ops(limit?: number): Promise<OutboxOp[]>;
  count(): Promise<number>;
  /**
   * Remove a settled op. When `ack` is given, also stamp the server's version
   * onto the local row so the next edit sends a correct `base_version`.
   */
  settle(op: OutboxOp, ack: { version: number; updated_seq: number } | null): Promise<void>;
  /** Move an op out of the queue and into the conflict / dead-letter list. */
  sideline(record: SidelinedOp): Promise<void>;
  listSidelined(kind: SidelineKind): Promise<SidelinedOp[]>;
  dropSidelined(kind: SidelineKind, opId: string): Promise<void>;
}

// ---------------------------------------------------------------------------
// IndexedDB-backed storage
// ---------------------------------------------------------------------------

function sidelineStore(kind: SidelineKind): StoreName {
  return kind === "conflict" ? CONFLICT_STORE : DEAD_STORE;
}

export const idbStorage: OutboxStorage = {
  async commit(entity, row, op) {
    const stores: StoreName[] = op ? [entity, OUTBOX_STORE] : [entity];
    await transaction(stores, "readwrite", async (tx) => {
      await tx.store(entity).put(row);
      if (op) {
        const { seq: _ignored, ...rest } = op;
        const key = await tx.store(OUTBOX_STORE).add(rest);
        op.seq = typeof key === "number" ? key : Number(key);
      }
    });
  },
  read(entity, id) {
    return transaction([entity], "readonly", (tx) => tx.store(entity).get<Row>(id));
  },
  readAll(entity) {
    return transaction([entity], "readonly", (tx) => tx.store(entity).getAll<Row>());
  },
  ops(limit) {
    return transaction([OUTBOX_STORE], "readonly", (tx) =>
      tx.store(OUTBOX_STORE).getAll<OutboxOp>(null, limit),
    );
  },
  count() {
    return transaction([OUTBOX_STORE], "readonly", (tx) => tx.store(OUTBOX_STORE).count());
  },
  async settle(op, ack) {
    const stores: StoreName[] = ack ? [OUTBOX_STORE, op.entity] : [OUTBOX_STORE];
    await transaction(stores, "readwrite", async (tx) => {
      if (op.seq !== undefined) await tx.store(OUTBOX_STORE).delete(op.seq);
      if (!ack) return;
      const row = await tx.store(op.entity).get<Row>(op.id);
      if (row) {
        await tx.store(op.entity).put({ ...row, version: ack.version, updated_seq: ack.updated_seq });
      }
    });
  },
  async sideline(record) {
    const target = sidelineStore(record.kind);
    await transaction([OUTBOX_STORE, target], "readwrite", async (tx) => {
      if (record.op.seq !== undefined) await tx.store(OUTBOX_STORE).delete(record.op.seq);
      await tx.store(target).put(record);
    });
  },
  listSidelined(kind) {
    const target = sidelineStore(kind);
    return transaction([target], "readonly", (tx) => tx.store(target).getAll<SidelinedOp>());
  },
  dropSidelined(kind, id) {
    const target = sidelineStore(kind);
    return transaction([target], "readwrite", (tx) => tx.store(target).delete(id));
  },
};

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let storage: OutboxStorage = idbStorage;

/** Swap the storage backend. Used by tests and by the in-memory fallback. */
export function setOutboxStorage(next: OutboxStorage): void {
  storage = next;
}

export function getOutboxStorage(): OutboxStorage {
  return storage;
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribe to outbox changes — drives the always-visible unsynced badge. */
export function subscribeOutbox(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) fn();
}

let paused = false;
let pauseReason: string | null = null;

/**
 * Pause draining without touching the queue. Auth failure, session expiry and
 * app upgrade all land here. The outbox is NEVER cleared on auth failure: the
 * user's unsynced ratings outlive their login.
 */
export function pauseOutbox(reason: string): void {
  paused = true;
  pauseReason = reason;
  notify();
}

export function resumeOutbox(): void {
  paused = false;
  pauseReason = null;
  notify();
}

export function outboxPaused(): { paused: boolean; reason: string | null } {
  return { paused, reason: pauseReason };
}

// ---------------------------------------------------------------------------
// Write path
// ---------------------------------------------------------------------------

/** Only fired once per session; `navigator.storage.persist()` is idempotent. */
let persistenceRequested = false;

async function requestPersistence(): Promise<void> {
  if (persistenceRequested) return;
  persistenceRequested = true;
  try {
    // A heuristic, not a guarantee: Safari can still evict a "persisted"
    // origin, and a false result does not mean data will be evicted soon.
    // This is why `exportJson` exists.
    await navigator.storage?.persist?.();
  } catch {
    // Unsupported or blocked; nothing to do, and nothing depends on it.
  }
}

/**
 * Write a change locally and queue it for the server.
 *
 * @param entity  Syncable entity the row belongs to.
 * @param id      Row id — client-generated with `ulid()` for creates.
 * @param patch   Fields to write. Absent fields are left untouched.
 * @param baseVersion Version the caller believed it was editing; 0 = create.
 * @returns the locally stored row as the UI should now render it.
 */
export async function mutate(
  entity: Entity,
  id: string,
  patch: Record<string, unknown>,
  baseVersion: number,
  options: { deleted?: boolean } = {},
): Promise<Row> {
  void requestPersistence();

  const existing = await storage.read(entity, id);
  // Every op already queued for this row will bump the server's version by
  // exactly one before this one is applied, so this op has to sit one above the
  // highest base already queued for it.
  //
  // Without that a correction made before the first push settles arrives with a
  // base the server has already moved past and comes back as a conflict: the
  // player rates 4, corrects to 6, the table reveals 4. Offline, that is every
  // re-rating, and every reorder-then-edit in settings.
  //
  // Taken as a `max` with the caller's base rather than as a count, because the
  // local version can advance underneath a still-queued op: a push whose
  // response was lost comes back `duplicate`, `settle` stamps the acknowledged
  // version on the row, and a queue depth added to that already-advanced base
  // over-shoots by one — the next correction is refused as a conflict and the
  // table reveals the uncorrected number.
  const queued = await storage.ops();
  const stacked = queued.filter((op) => op.entity === entity && op.id === id);
  const highestQueued = stacked.reduce((top, op) => Math.max(top, op.base_version), -1);
  const opBaseVersion = Math.max(baseVersion, highestQueued + 1);
  const now = Date.now();
  const row: Row = {
    // Sensible SyncMeta defaults for a create; a patch may override any of them.
    version: baseVersion,
    updated_seq: 0,
    deleted: 0,
    ...existing,
    ...patch,
    id,
    updated_at: now,
  };
  if (options.deleted) row.deleted = 1;

  const op: OutboxOp = {
    // A client-generated key so a retry after a lost response is deduplicated
    // server-side instead of applied twice.
    op_id: opId(),
    entity,
    id,
    base_version: opBaseVersion,
    patch: { ...patch },
    created_at: now,
    ...(options.deleted ? { deleted: true } : {}),
  };

  await storage.commit(entity, row, op);
  notify();
  return row;
}

/**
 * Tombstone a row. Deletes replicate like any other write.
 *
 * The tombstone rides in the envelope's `deleted` flag and NOWHERE else. The
 * server validates patch keys against the target table's columns minus a
 * reserved set (`id`, `owner_id`, `version`, `updated_seq`, `deleted`,
 * `updated_at`) — see `validPatchColumns` in `src/server/sync.ts`. Putting
 * `deleted` in the patch as well made every delete apply locally and then
 * dead-letter on the way up: the row vanished from the user's screen and came
 * back as an unreadable dead letter.
 */
export function remove(entity: Entity, id: string, baseVersion: number): Promise<Row> {
  return mutate(entity, id, {}, baseVersion, { deleted: true });
}

/**
 * Tombstone a row at whatever version the server has actually reached.
 *
 * `remove(entity, id, 0)` is only ever right for a row the server has never
 * heard of, and a caller undoing something it created a moment ago is exactly
 * the caller most likely to believe that wrongly. A row that has been pushed
 * comes back stamped — `settle` writes the server's `version` onto it — so a
 * delete sent at base 0 against a row the server holds at 1 is refused as a
 * conflict (`applyOne` in `src/server/sync.ts`). That is how a failed
 * "Start rating" left a phantom game on every other device while the table was
 * told nothing had been saved.
 *
 * The base is the last version this device saw acknowledged; `mutate` then
 * stacks it above anything still queued for the same row, so this is equally
 * correct for a create that has not been pushed yet — the pair travels together
 * and nets out.
 *
 * Returns null when there is no local row to tombstone, which is not a failure:
 * nothing here can leave a row behind that was never written.
 */
export async function removeCurrent(entity: Entity, id: string): Promise<Row | null> {
  const existing = await storage.read(entity, id);
  if (!existing) return null;
  const version = existing["version"];
  return mutate(entity, id, {}, typeof version === "number" ? version : 0, { deleted: true });
}

/** Read a local row without touching the network. */
export function readLocal(entity: Entity, id: string): Promise<Row | undefined> {
  return storage.read(entity, id);
}

/** Ops waiting to be pushed, oldest first. */
export function pending(limit = 200): Promise<OutboxOp[]> {
  return storage.ops(limit);
}

/** Backing number for the always-visible unsynced indicator. */
export function pendingCount(): Promise<number> {
  return storage.count();
}

// ---------------------------------------------------------------------------
// Result routing
// ---------------------------------------------------------------------------

export interface RoutingSummary {
  applied: number;
  duplicate: number;
  conflict: number;
  rejected: number;
  /**
   * The refused ops themselves, so the caller can put their rows back.
   *
   * Dead-lettering keeps the *op* where the user can see it, which was only
   * ever half the job: the row it wrote optimistically is still sitting in
   * IndexedDB saying something the server refused. The transport is the layer
   * that can ask the server what the row really says, so the list goes up to it
   * rather than the network coming down here.
   */
  rejectedOps: OutboxOp[];
  /** Ops the server said nothing about; they stay queued for the next push. */
  unanswered: number;
}

/**
 * Apply a push response to the queue.
 *
 * - `applied` / `duplicate` -> op removed, local row stamped with the server
 *   version so the next edit carries a correct `base_version`.
 * - `conflict` -> op moved to the conflicts list together with the server row.
 *   Neither value is discarded; the user picks.
 * - `rejected` -> op moved to the dead-letter list with the server's reason.
 */
export async function applyResults(
  ops: readonly OutboxOp[],
  results: readonly MutationResult[],
): Promise<RoutingSummary> {
  const byId = new Map(ops.map((op) => [op.op_id, op] as const));
  const summary: RoutingSummary = {
    applied: 0,
    duplicate: 0,
    conflict: 0,
    rejected: 0,
    rejectedOps: [],
    unanswered: 0,
  };
  const answered = new Set<string>();

  for (const result of results) {
    const op = byId.get(result.op_id);
    if (!op) continue; // Not ours (or already settled by a concurrent drain).
    answered.add(result.op_id);
    switch (result.status) {
      case "applied":
      case "duplicate":
        await storage.settle(op, { version: result.version, updated_seq: result.updated_seq });
        summary[result.status] += 1;
        break;
      case "conflict":
        await storage.sideline({
          op_id: op.op_id,
          kind: "conflict",
          op,
          at: Date.now(),
          server_version: result.server_version,
          server_row: result.server_row,
        });
        summary.conflict += 1;
        break;
      case "rejected":
        await storage.sideline({
          op_id: op.op_id,
          kind: "dead",
          op,
          at: Date.now(),
          reason: result.reason,
        });
        summary.rejected += 1;
        summary.rejectedOps.push(op);
        break;
    }
  }

  summary.unanswered = ops.filter((op) => !answered.has(op.op_id)).length;
  notify();
  return summary;
}

/** Conflicts the user still has to resolve. Surfaced, never swallowed. */
export function conflicts(): Promise<SidelinedOp[]> {
  return storage.listSidelined("conflict");
}

/** Ops the server refused outright. Kept so a bug is visible, not invisible. */
export function deadLetters(): Promise<SidelinedOp[]> {
  return storage.listSidelined("dead");
}

export type Resolution = "keep_mine" | "take_theirs";

/**
 * Resolve one conflict.
 *
 * `keep_mine` re-queues the same patch on top of the server's version under a
 * FRESH op_id — the old op_id may already be recorded server-side, so reusing
 * it would be deduplicated into a no-op.
 * `take_theirs` writes the server row locally and queues nothing.
 */
export async function resolveConflict(id: string, resolution: Resolution): Promise<void> {
  const all = await storage.listSidelined("conflict");
  const record = all.find((c) => c.op_id === id);
  if (!record) return;

  if (resolution === "take_theirs") {
    if (record.server_row) await storage.commit(record.op.entity, record.server_row, null);
  } else {
    const base = record.server_row ?? {};
    // The tombstone lives in the envelope, not the patch, so it has to be
    // re-applied by hand here. This is a local row write, where `deleted` is a
    // perfectly ordinary column.
    const row: Row = {
      ...base,
      ...record.op.patch,
      ...(record.op.deleted ? { deleted: 1 } : {}),
      id: record.op.id,
      updated_at: Date.now(),
    };
    const retry: OutboxOp = {
      ...record.op,
      op_id: opId(),
      seq: undefined,
      base_version: record.server_version ?? record.op.base_version,
      created_at: Date.now(),
    };
    await storage.commit(record.op.entity, row, retry);
  }
  await storage.dropSidelined("conflict", id);
  notify();
}

/** Drop a dead letter once the user has acknowledged it. */
export async function discardDeadLetter(id: string): Promise<void> {
  await storage.dropSidelined("dead", id);
  notify();
}

/** Re-queue a dead letter after a fix (e.g. the app was upgraded). */
export async function retryDeadLetter(id: string): Promise<void> {
  const all = await storage.listSidelined("dead");
  const record = all.find((d) => d.op_id === id);
  if (!record) return;
  const retry: OutboxOp = { ...record.op, op_id: opId(), seq: undefined };
  // The patch is re-applied rather than read off the row. A refusal now rolls
  // the local row back to what the server holds (`push` in `sync.ts`), so "the
  // row as it stands" is the opposite of what this op is asking for — taking it
  // as-is would re-queue the write while the screen went on showing the value
  // it is trying to replace.
  const row: Row = {
    id: record.op.id,
    ...(await storage.read(record.op.entity, record.op.id)),
    ...record.op.patch,
    ...(record.op.deleted ? { deleted: 1 } : {}),
  };
  await storage.commit(record.op.entity, row, retry);
  await storage.dropSidelined("dead", id);
  notify();
}

// ---------------------------------------------------------------------------
// Roster ranking (plan decision 22)
// ---------------------------------------------------------------------------

/** One co-player as the participant picker should rank and render them. */
export interface CoPlayerStanding {
  profile_id: string;
  /** Newest snapshot seen for this profile, so the picker needs no second read. */
  name: string;
  colour: string;
  is_guest: 0 | 1;
  /** Sessions this profile took part in. Raw, undecayed. */
  plays: number;
  /** `played_at` of their most recent session, epoch ms. 0 when unknown. */
  last_played_at: number;
  /** Recency-weighted play count; the value the list is sorted on. */
  score: number;
}

/**
 * How fast a play stops counting. At one half-life old a play is worth half a
 * play, at two half-lives a quarter. Chosen so that a group you played weekly
 * last spring still outranks a one-off from two years ago, but this month's
 * group outranks last spring's.
 */
const RECENCY_HALF_LIFE_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Co-player standings from the local store, best first.
 *
 * Decision 22 asks the participant picker to lead with frequent co-players
 * *and* weight recency. Frequency alone re-offers the group you played weekly
 * three years ago ahead of the group you played last Friday. So the ranking is
 * a sum of per-play recency weights rather than a raw count: every play decays
 * on `RECENCY_HALF_LIFE_MS`, and the decayed total is the sort key.
 *
 * The policy lives here on purpose. Every screen that offers a roster must
 * order it the same way, and `plays` / `last_played_at` are returned alongside
 * `score` so a screen can *show* the reasoning without re-deriving it.
 *
 * Reads only; queues nothing. Tombstoned participants and tombstoned sessions
 * are ignored — a removed player must not keep earning rank.
 *
 * @param options.now Clock override. Tests pin it; callers should not pass it.
 */
export async function coPlayerStandings(
  options: { now?: number } = {},
): Promise<CoPlayerStanding[]> {
  const readAll = storage.readAll?.bind(storage);
  if (!readAll) return [];

  const [participants, sessions] = await Promise.all([
    readAll("session_participant"),
    readAll("play_session"),
  ]);

  const playedAt = new Map<string, number>();
  for (const session of sessions) {
    if (session["deleted"] === 1) continue;
    const id = session["id"];
    if (typeof id !== "string") continue;
    const at = session["played_at"];
    playedAt.set(id, typeof at === "number" ? at : 0);
  }

  const now = options.now ?? Date.now();
  const standings = new Map<string, CoPlayerStanding>();
  // Newest snapshot wins the name/colour, so a rename shows through.
  const snapshotAt = new Map<string, number>();

  for (const participant of participants) {
    if (participant["deleted"] === 1) continue;
    const profileId = participant["profile_id"];
    const sessionId = participant["session_id"];
    if (typeof profileId !== "string" || typeof sessionId !== "string") continue;
    // A participant whose session is absent or tombstoned is not a play.
    const at = playedAt.get(sessionId);
    if (at === undefined) continue;

    let standing = standings.get(profileId);
    if (!standing) {
      standing = {
        profile_id: profileId,
        name: "",
        colour: "",
        is_guest: 0,
        plays: 0,
        last_played_at: 0,
        score: 0,
      };
      standings.set(profileId, standing);
    }

    standing.plays += 1;
    if (at > standing.last_played_at) standing.last_played_at = at;
    // A play in the future (clock skew on a shared device) counts as fresh
    // rather than as worth more than a play.
    const age = Math.max(0, now - at);
    standing.score += Math.pow(0.5, age / RECENCY_HALF_LIFE_MS);

    const seen = snapshotAt.get(profileId) ?? -1;
    if (at >= seen) {
      snapshotAt.set(profileId, at);
      const name = participant["name_snapshot"];
      const colour = participant["colour_snapshot"];
      if (typeof name === "string") standing.name = name;
      if (typeof colour === "string") standing.colour = colour;
      standing.is_guest = participant["is_guest"] === 1 ? 1 : 0;
    }
  }

  // Name breaks the final tie so the picker does not reshuffle between renders.
  return [...standings.values()].sort(
    (a, b) =>
      b.score - a.score ||
      b.last_played_at - a.last_played_at ||
      b.plays - a.plays ||
      a.name.localeCompare(b.name),
  );
}

// ---------------------------------------------------------------------------
// In-memory storage — used by tests and as a degraded fallback when the
// browser refuses IndexedDB (Safari private mode has historically done so).
// ---------------------------------------------------------------------------

export function createMemoryStorage(): OutboxStorage {
  const rows = new Map<string, Map<string, Row>>();
  const queue: OutboxOp[] = [];
  const sidelined = new Map<SidelineKind, Map<string, SidelinedOp>>([
    ["conflict", new Map()],
    ["dead", new Map()],
  ]);
  let nextSeq = 1;

  const table = (entity: Entity): Map<string, Row> => {
    let t = rows.get(entity);
    if (!t) {
      t = new Map();
      rows.set(entity, t);
    }
    return t;
  };

  return {
    async commit(entity, row, op) {
      // Snapshot-and-restore stands in for a transaction: if appending the op
      // throws, the row write must not survive either.
      const t = table(entity);
      const before = t.get(String(row.id));
      try {
        t.set(String(row.id), { ...row });
        if (op) {
          op.seq = nextSeq++;
          queue.push({ ...op });
        }
      } catch (err) {
        if (before) t.set(String(row.id), before);
        else t.delete(String(row.id));
        throw err;
      }
    },
    async read(entity, id) {
      const found = table(entity).get(id);
      return found ? { ...found } : undefined;
    },
    async readAll(entity) {
      return [...table(entity).values()].map((row) => ({ ...row }));
    },
    async ops(limit) {
      const sorted = [...queue].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
      return (limit === undefined ? sorted : sorted.slice(0, limit)).map((op) => ({ ...op }));
    },
    async count() {
      return queue.length;
    },
    async settle(op, ack) {
      const at = queue.findIndex((q) => q.op_id === op.op_id);
      if (at >= 0) queue.splice(at, 1);
      if (!ack) return;
      const t = table(op.entity);
      const row = t.get(op.id);
      if (row) t.set(op.id, { ...row, version: ack.version, updated_seq: ack.updated_seq });
    },
    async sideline(record) {
      const at = queue.findIndex((q) => q.op_id === record.op.op_id);
      if (at >= 0) queue.splice(at, 1);
      sidelined.get(record.kind)?.set(record.op_id, { ...record });
    },
    async listSidelined(kind) {
      return [...(sidelined.get(kind)?.values() ?? [])].map((r) => ({ ...r }));
    },
    async dropSidelined(kind, id) {
      sidelined.get(kind)?.delete(id);
    },
  };
}
